import { ChangeDetectorRef, Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { ITenant } from '@app/services/tenant/models/tenant.model';
import { NavController } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { OrderPipe } from 'ngx-order-pipe';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ITenantLite } from 'src/app/services/tenant/models/tenant-lite.model';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { UserService } from 'src/app/services/user/user.service';

@Component({
  selector: 'app-tenant-select',
  templateUrl: './tenant-select.page.html',
  styleUrls: ['./tenant-select.page.scss'],
})
export class TenantSelectPage implements OnInit {
  public isOnline = this.networkService.hasInternetAccess;
  private logger: Logger;
  private registrationCode: string = null;
  private changeHomeClicked: boolean = false;
  public hasSelectedTenant: boolean = false
  public isCordova: boolean = this.platformService.isCordova();
  public  showDotsSignal = signal<boolean>(true);
  public  tenantListSignal = signal<ITenantLite[]>([]);
  private tenantDomain = this.tenantService.tenantDomain;
  private environmentDomain = this.tenantService.environmentDomain;

  constructor(
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private loggingService: LoggingService,
    private navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    private platformService: PlatformService,
    public orderPipe: OrderPipe,
    private storageService: StorageService,
    private tenantService: TenantService,
    private userService: UserService
  ) {
    this.logger = loggingService.getLogger("[TenantSelectPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('ChangeHome');
    if (this.isCordova) {
      this.storageService.getDatabaseState().pipe(
        filter(isReady => isReady),
        take(1)
      ).subscribe(() => this.initTenantSelection());
    } else {
      this.initTenantSelection();
    }
  }

  public initTenantSelection(): void {
    this.route.queryParams.pipe(
      take(1),
      switchMap(params => {
        console.log('PARAMS params', params);
        this.registrationCode = params['registrationCode'] || null;
        this.changeHomeClicked = params['changeHome'] || false;
        return this.getTenantListForUser$();
      }),
      catchError(e => {
        this.logger.info('Error in initTenantSelection: ' + e);
        return of(null);
      })
    ).subscribe((tenant: ITenantLite | null) => {
      if (!!tenant) {
        this.selectTenant(tenant);
      }
    });
  }

  public hasPreSelectedTenant = computed(() => {
    console.log('this.changeHomeClicked', this.changeHomeClicked);
    if (this.changeHomeClicked) {
      return false;
    }
    return (this.tenantDomain && !this.environmentDomain) ? true : false;
  })

  public getTenantListForUser$(refresher?): Observable<any> {
    this.logger.info('getTenantListForUser$()');
    return this.tenantService.getTenantListForUser().pipe(
      take(1),
      tap((tenantList: ITenantLite[]) => {
        this.logger.info('got TenantList with length: ' + tenantList.length);
        if (refresher) {
          refresher.complete();
        }
      }),
      switchMap((tenantList: ITenantLite[]) => {
        this.logger.info('getTenantListForUser$() - GOT tenantList');

        // If no tenants, update signals and return null
        if (!tenantList || tenantList.length === 0) {
          this.tenantListSignal.set([]);
          this.showDotsSignal.set(false);
          return of(null);
        }

        if (this.changeHomeClicked) {
          return this.showTenantList$(tenantList);
        } else if (tenantList) {
          return this.getLastUsedTenant$().pipe(
            take(1),
            switchMap((lastUsedTenant: ITenantLite) => {
              if (lastUsedTenant) {
                this.logger.info('got lastUsedTenant hostingDomain: ' + JSON.stringify(lastUsedTenant['tenantData']['hostingDomain']));
                this.logger.info('got lastUsedTenant Url: ' + JSON.stringify(lastUsedTenant['links']['self']));
                return of(lastUsedTenant);
              } else {
                this.logger.info('no lastUsedTenant');
                return this.handleTenantSelection$(tenantList)
              }
            })
          );
        }
      }),
      catchError(e => {
        this.logger.error('Error getting tenant list: ' + e);
        this.tenantListSignal.set([]);
        this.showDotsSignal.set(false);
        return of(null);
      })
    )
  }

  private showTenantList$(tenantList: ITenantLite[]): Observable<void> {
    this.logger.info('## showTenantList() - no tenant from user input or changeHomeClicked ##');
    // show tenant list in UI
    this.tenantListSignal.set(tenantList);
    // hide loading dots
    this.showDotsSignal.set(false);
    // make sure templated data is updated
    this.changeDetectorRef.detectChanges();
    // no further actions until user selects a tenant
    return of(null);
  }

  private getLastUsedTenant$(): Observable<ITenantLite | null> {
    this.logger.info('getLastUsedTenant$()');
    return this.storageService.getItemFromLocalStorage('lastUsedTenant', 'getLastUsedTenant()').pipe(
      take(1),
      switchMap((lastUsedTenant: ITenantLite) => {
        return lastUsedTenant ? of(lastUsedTenant) : of(null);
      }),
      catchError(e => {
        this.logger.info('error getting lastUsedTenant: ' + e);
        return of(null);
      })
    );
  }

  private handleTenantSelection$(tenantList: ITenantLite[]): Observable<ITenantLite | void> {
    this.logger.info('handleTenantSelection$()');

    // first check if tenantDomain is set by user input
    if (this.tenantDomain && !this.environmentDomain) {
      const userTenant = this.findTenantFromHostingDomain(this.tenantDomain, tenantList);
      // if tenant found in tenantList select tenant
      if (userTenant) {
        return of(userTenant);
      } else {
        // if no tenant found with user input tenantDomain, show alert
        this.alertNoMatchingTenantInTenantList(this.tenantDomain);
        // error handling: no tenant found with user input tenantDomain
        return of(null);
      }
    } else if (tenantList.length === 1) {
      // if no tenant from user input and returned tenantList has only one tenant, select this tenant
      return of(tenantList[0]);
    } else {
      return this.showTenantList$(tenantList);
    }
  }

  private findTenantFromHostingDomain(tenantHostingDomain: string, tenantList: ITenantLite[]): ITenantLite {
    this.logger.info('findTenantFromHostingDomain()');
    let userTenant: ITenantLite;
    tenantList.forEach(tenant => {
      if (tenant.tenantData.hostingDomain === tenantHostingDomain) {
        userTenant = tenant;
      }
    });
    return userTenant;
  }

  private alertNoMatchingTenantInTenantList(tenantHostingDomain): void {
    this.logger.info('No matching hosting domain for Tenant url: ' + JSON.stringify(tenantHostingDomain));
    this.dialogService.showAlertDialog('No matching hosting domain for Tenant url, if you are a rw-user you cannot access the release app: ' + tenantHostingDomain)
    .subscribe(() => {
      this.showDotsSignal.set(false);
    });
  }

  // #################################### //
  // ### PROCEED WITH SELECTED TENANT ### //
  // #################################### //

  public selectTenant(tenant: ITenantLite): void {
    this.logger.info('selectTenant(): ' + JSON.stringify(tenant['tenantData']['name']));
    !this.showDotsSignal() ? this.showDotsSignal.set(true) : null;
    this.tenantService.getTenantResource(tenant.links.self).pipe(
      take(1),
        concatMap((fullTenant: ITenant) =>
          this.userService.getUserResource(fullTenant.links.currentUser).pipe(
            take(1),
            map(() => (fullTenant))
          )
        ),
        concatMap((fullTenant) =>
          this.storageService.addItemToLocalStorage('lastUsedTenant', fullTenant, 'selectTenant()').pipe(take(1))
        )
    ).subscribe({
      next: () => {
        this.continueToApp();
      },
      error: err => {
        this.logger.error('selectTenant() - Error refreshing tenant or user: ' + JSON.stringify(err));
        this.dialogService.showAlertDialog('Error getting tenant or user data.')
        .subscribe(() => {
          this.showDotsSignal.set(false);
        });
      }
    });
  }

  private continueToApp(): void {
    this.logger.info('continueToApp()');
    const path = this.registrationCode ? '/go' : '/print-jobs';
    const queryParams = this.registrationCode ? { registrationCode: this.registrationCode } : {};
    this.navController.navigateRoot([path], { queryParams });
  }
}
